import React, { useState } from 'react';
import { createTip, deleteGuideMedia, deleteTip, reorderTips, updateTip } from '../../../../api/stays-guide-client';
import { StaysApiError } from '../../../../api/stays-client';
import type { GuideMedia, GuideTip, GuideTipInput, GuideTipKind } from '../../../../interfaces/stays-guide';
import { AppButton } from '../../../button/app-button';
import { CountedTextarea } from '../../../counted-textarea/counted-textarea';
import { Field, FieldError, Input, Label } from '../../../form-kit';
import { ConfirmDialog } from '../../../modal/confirm-dialog';
import { FormModal } from '../../../modal/form-modal';
import { SegmentedControl } from '../../../segmented-control/segmented-control';
import { ProgressFill, ProgressTrack } from '../../../gallery/entity-gallery.styled';
import { ActionWithReason } from '../../dashboard/dashboard-shared-components/disabled-reason';
import { ERROR_COPY, refusalText } from '../../dashboard/dashboard-stays-components/stays-labels';
import toast from '../../../../utils/toast';
import { Counter, FileButton, H2, H3, HiddenFile, Item, List, Muted, Row, Section, SectionHead, Text, Thumb } from './guide-editor.styled';
import { Frame } from './guide-photos-editor.styled';
import { CODE_IN_TEXT_COPY, TIP_KINDS, tipKindLabel } from './guide-model';
import { ACCEPT_PHOTOS, MediaUpdate, uploadCompressed, uploadErrorText } from './guide-upload';

/**
 * THE LOCAL TIPS TAB (plan S7.8): up to 60 places the host recommends,
 * grouped by kind (Eat / See / Nearby / Transport / Other), ordered within a
 * kind. Add and Edit share one FormModal; a tip's optional photo is uploaded
 * with the same compressor (slot 'tip') and linked by its id on save. Guests
 * see the section only when there is at least one tip.
 */

type TipUpdate = (update: (current: GuideTip[]) => GuideTip[]) => void;

interface Draft {
  kind: GuideTipKind;
  name: string;
  note: string;
  address: string;
  maps_url: string;
  phone: string;
  media_id: string | null;
}

const EMPTY: Draft = { kind: 'eat', name: '', note: '', address: '', maps_url: '', phone: '', media_id: null };

const draftOf = (t: GuideTip): Draft => ({
  kind: t.kind,
  name: t.name,
  note: t.note || '',
  address: t.address || '',
  maps_url: t.maps_url || '',
  phone: t.phone || '',
  media_id: t.media_id,
});

const sameDraft = (a: Draft, b: Draft) => JSON.stringify(a) === JSON.stringify(b);

export interface LocalTipsEditorProps {
  listingId: string;
  tips: GuideTip[];
  media: GuideMedia[];
  limit: number;
  canManage: boolean;
  onTipsChange: TipUpdate;
  onMediaChange: MediaUpdate;
  onReload: () => void | Promise<void>;
}

export const LocalTipsEditor: React.FC<LocalTipsEditorProps> = ({
  listingId,
  tips,
  media,
  limit,
  canManage,
  onTipsChange,
  onMediaChange,
  onReload,
}) => {
  const [editing, setEditing] = useState<GuideTip | 'new' | null>(null);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [initial, setInitial] = useState<Draft>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [photoPct, setPhotoPct] = useState<number | null>(null);
  /** Photos uploaded in THIS modal, not yet saved on a tip: removed if abandoned. */
  const [fresh, setFresh] = useState<string[]>([]);
  const [deleting, setDeleting] = useState<GuideTip | null>(null);
  const [moving, setMoving] = useState(false);

  const full = tips.length >= limit;
  const photoOf = (id: string | null) => (id ? media.find(m => m.id === id) || null : null);

  const open = (t: GuideTip | 'new') => {
    const d = t === 'new' ? EMPTY : draftOf(t);
    setDraft(d);
    setInitial(d);
    setErrors({});
    setFresh([]);
    setEditing(t);
  };

  const discardFresh = (keep: string | null) => {
    for (const id of fresh) {
      if (id === keep) continue;
      deleteGuideMedia(listingId, id).catch(() => undefined);
      onMediaChange(cur => cur.filter(m => m.id !== id));
    }
    setFresh([]);
  };

  const close = () => {
    if (busy || photoPct !== null) return;
    discardFresh(null);
    setEditing(null);
  };

  const addPhoto = async (file: File | undefined) => {
    if (!file || photoPct !== null) return;
    setPhotoPct(0);
    try {
      const m = await uploadCompressed(listingId, file, { slot: 'tip' }, setPhotoPct);
      onMediaChange(cur => [...cur, m]);
      setFresh(f => [...f, m.id]);
      setDraft(d => ({ ...d, media_id: m.id }));
    } catch (err) {
      toast.error(uploadErrorText(err));
    } finally {
      setPhotoPct(null);
    }
  };

  const save = async () => {
    if (!editing || busy) return;
    if (!draft.name.trim()) {
      setErrors({ name: 'Write the name of the place.' });

      return;
    }
    setBusy(true);
    setErrors({});
    const body: GuideTipInput = {
      kind: draft.kind,
      name: draft.name.trim(),
      note: draft.note.trim() || null,
      address: draft.address.trim() || null,
      maps_url: draft.maps_url.trim() || null,
      phone: draft.phone.trim() || null,
      media_id: draft.media_id,
    };
    try {
      const before = editing === 'new' ? null : editing.media_id;
      if (editing === 'new') {
        const { tip } = await createTip(listingId, body);
        onTipsChange(cur => [...cur, tip]);
        toast.success('Tip added');
      } else {
        const { tip } = await updateTip(editing.id, body);
        onTipsChange(cur => cur.map(t => (t.id === tip.id ? tip : t)));
        toast.success('Tip saved');
      }
      // The replaced photo is deleted by the server with the change (S7 dropTipPhoto);
      // any uploaded here but not kept is dropped below.
      if (before && before !== draft.media_id) {
        onMediaChange(cur => cur.filter(m => m.id !== before));
      }
      discardFresh(draft.media_id);
      setEditing(null);
    } catch (err) {
      const e = err as StaysApiError;
      if (e && e.code === 'CODE_IN_TEXT') {
        const field = e.field || 'note';
        setErrors({ [field]: CODE_IN_TEXT_COPY });
      } else if (e && e.status === 400 && Object.keys(e.fields || {}).length) {
        setErrors(e.fields);
      } else {
        toast.error(refusalText(e, 'Could not save the tip. Try again.'));
      }
    } finally {
      setBusy(false);
    }
  };

  /** Within its kind: swap with the neighbour of the same kind, send the whole order. */
  const move = async (tip: GuideTip, delta: number) => {
    if (moving) return;
    const same = tips.filter(t => t.kind === tip.kind);
    const at = same.findIndex(t => t.id === tip.id);
    const other = same[at + delta];
    if (!other) return;
    const i = tips.findIndex(t => t.id === tip.id);
    const j = tips.findIndex(t => t.id === other.id);
    const next = tips.slice();
    next[i] = other;
    next[j] = tip;
    setMoving(true);
    try {
      const out = await reorderTips(listingId, next.map(t => t.id));
      onTipsChange(() => out.tips);
    } catch (err) {
      const e = err as StaysApiError;
      toast.error(refusalText(e, 'Could not move the tip. Try again.'));
      if (e && e.code === 'LIST_CHANGED') void onReload();
    } finally {
      setMoving(false);
    }
  };

  const remove = async (tip: GuideTip) => {
    try {
      await deleteTip(tip.id);
      onTipsChange(cur => cur.filter(t => t.id !== tip.id));
      // The tip's own photo goes with it on the server (S7 dropTipPhoto).
      if (tip.media_id) {
        const photo = tip.media_id;
        onMediaChange(cur => cur.filter(m => m.id !== photo));
      }
      toast.success('Tip deleted');
    } catch (err) {
      toast.error(refusalText(err as StaysApiError, 'Could not delete the tip. Try again.'));
    }
  };

  const text = (k: 'name' | 'address' | 'maps_url' | 'phone', label: string, max: number, type = 'text') => (
    <Field>
      <Label htmlFor={`tip-${k}`}>{label}</Label>
      <Input
        id={`tip-${k}`}
        type={type}
        value={draft[k]}
        maxLength={max}
        hasError={!!errors[k]}
        aria-describedby={errors[k] ? `tip-${k}-error` : undefined}
        onChange={e => setDraft({ ...draft, [k]: e.target.value })}
      />
      <FieldError id={`tip-${k}-error`} message={errors[k]} />
    </Field>
  );

  const draftPhoto = photoOf(draft.media_id);

  return (
    <Section aria-label="Local tips">
      <SectionHead>
        <Counter data-testid="tips-count">{`${tips.length} of ${limit}`}</Counter>
        {canManage ? (
          <ActionWithReason reason={full ? ERROR_COPY.TIP_LIMIT : null}>
            {reasonId => (
              <AppButton variant="secondary" disabled={full} aria-describedby={reasonId} onClick={() => open('new')}>
                Add a tip
              </AppButton>
            )}
          </ActionWithReason>
        ) : null}
      </SectionHead>

      {!tips.length ? (
        <Muted role="status">No local tips yet. Guests see this section only when you add some.</Muted>
      ) : (
        TIP_KINDS.filter(k => tips.some(t => t.kind === k.value)).map(k => {
          const group = tips.filter(t => t.kind === k.value);

          return (
            <section key={k.value} aria-labelledby={`tips-kind-${k.value}`} data-kind={k.value}>
              <H2 id={`tips-kind-${k.value}`}>{k.label}</H2>
              <List>
                {group.map((t, i) => {
                  const photo = photoOf(t.media_id);
                  const nameId = `tip-name-${t.id}`;

                  return (
                    <Item key={t.id} data-tip={t.id}>
                      {photo ? (
                        <Frame>
                          <Thumb src={photo.url} alt={t.name} loading="lazy" />
                        </Frame>
                      ) : null}
                      <H3 id={nameId}>{t.name}</H3>
                      {t.note ? <Text>{t.note}</Text> : null}
                      {t.address ? <Muted>{t.address}</Muted> : null}
                      {t.phone ? <Muted>{t.phone}</Muted> : null}
                      {canManage ? (
                        <Row>
                          <AppButton variant="ghost" size="compact" aria-label={`Edit ${t.name}`} onClick={() => open(t)}>
                            Edit
                          </AppButton>
                          <AppButton variant="ghost" size="compact" aria-label={`Delete ${t.name}`} onClick={() => setDeleting(t)}>
                            Delete
                          </AppButton>
                          <AppButton variant="ghost" size="compact" aria-describedby={nameId} disabled={i === 0 || moving}
                            onClick={() => void move(t, -1)}>
                            Move up
                          </AppButton>
                          <AppButton variant="ghost" size="compact" aria-describedby={nameId}
                            disabled={i === group.length - 1 || moving} onClick={() => void move(t, 1)}>
                            Move down
                          </AppButton>
                        </Row>
                      ) : null}
                    </Item>
                  );
                })}
              </List>
            </section>
          );
        })
      )}

      <FormModal
        open={!!editing}
        title={editing === 'new' ? 'Add a tip' : 'Edit tip'}
        onClose={close}
        isDirty={!sameDraft(draft, initial)}
        saving={busy || photoPct !== null}
        footer={
          <>
            <AppButton variant="ghost" disabled={busy} onClick={close}>
              Cancel
            </AppButton>
            <AppButton variant="primary" loading={busy} loadingLabel="Saving…"
              disabled={photoPct !== null} onClick={() => void save()}>
              Save tip
            </AppButton>
          </>
        }
      >
        <form
          id="tip-form"
          noValidate
          onSubmit={e => {
            e.preventDefault();
            void save();
          }}
        >
          <Field>
            <Label as="span">Kind</Label>
            <SegmentedControl<GuideTipKind>
              label="Kind"
              options={TIP_KINDS}
              value={draft.kind}
              onChange={kind => setDraft({ ...draft, kind })}
            />
            <FieldError message={errors.kind} />
          </Field>
          {text('name', 'Name', 120)}
          <Field>
            <CountedTextarea id="tip-note" label="Note" value={draft.note} max={600} rows={3} hasError={!!errors.note}
              aria-describedby={errors.note ? 'tip-note-error' : undefined}
              onChange={e => setDraft({ ...draft, note: e.target.value })} />
            <FieldError id="tip-note-error" message={errors.note} />
          </Field>
          {text('address', 'Address', 200)}
          {text('maps_url', 'Maps link', 500, 'url')}
          {text('phone', 'Phone', 40, 'tel')}
          <Field>
            <Label as="span">Photo</Label>
            {draftPhoto ? (
              <Frame>
                <Thumb src={draftPhoto.url} alt={`Photo of ${draft.name || tipKindLabel(draft.kind)}`} />
              </Frame>
            ) : null}
            {photoPct !== null ? (
              <ProgressTrack role="progressbar" aria-label="Uploading the tip's photo" aria-valuemin={0} aria-valuemax={100}
                aria-valuenow={photoPct}>
                <ProgressFill $percent={photoPct} />
              </ProgressTrack>
            ) : null}
            <Row>
              <FileButton htmlFor="tip-photo" $disabled={photoPct !== null}>
                {draftPhoto ? 'Change photo' : 'Add a photo'}
                <HiddenFile
                  id="tip-photo"
                  type="file"
                  accept={ACCEPT_PHOTOS}
                  disabled={photoPct !== null}
                  onChange={e => {
                    const file = e.target.files ? e.target.files[0] : undefined;
                    e.target.value = '';
                    void addPhoto(file);
                  }}
                />
              </FileButton>
              {draftPhoto ? (
                <AppButton variant="ghost" size="compact" onClick={() => setDraft({ ...draft, media_id: null })}>
                  Remove the photo
                </AppButton>
              ) : null}
            </Row>
            <FieldError message={errors.media_id} />
          </Field>
        </form>
      </FormModal>

      <ConfirmDialog
        open={!!deleting}
        title={deleting ? `Delete ${deleting.name}?` : 'Delete tip?'}
        message="Guests stop seeing this tip at once."
        primaryLabel="Delete tip"
        primaryVariant="destructive"
        dismissLabel="Keep tip"
        onPrimary={async () => {
          if (deleting) await remove(deleting);
          setDeleting(null);
        }}
        onDismiss={() => setDeleting(null)}
      />
    </Section>
  );
};
